import { buildHearenProfileConfig } from '../services/hearenProfileEngine';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getSessionStopHandler } from '@/services/sessionStopRegistry';
import { Lock } from 'lucide-react';
import PersonalizationNudge from '@/components/PersonalizationNudge';
import useAdaptiveHome, { trackSessionStart } from '@/hooks/useAdaptiveHome';

import { V11_THEMES } from '@/config/themesV11';
import { registerPlugin } from '@capacitor/core';
import { resolveHearenState } from '@/matrix/hearenmatrixengine'
import { buildSatoriMatrix } from '../services/satoriMatrixEngine';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://hearen-backend.onrender.com';
const NativeAudio = registerPlugin('NativeAudio');

const HomeNew = ({ t }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.isPremium || user?.subscription === 'premium';
  const isFr = !t?.locale || t?.locale !== 'en-US';
  const adaptive = useAdaptiveHome(user, isFr);
  const [testsCompletedFromEqualizer, setTestsCompletedFromEqualizer] = useState(false);
  const [isOpeningCocoon, setIsOpeningCocoon] = useState(false);

  const [matrixListeningStats, setMatrixListeningStats] = useState({
    sessionsCount: 0,
    totalMinutes: 0,
    activeDays: 0,
  });
  const [matrixTinnitusFrequency, setMatrixTinnitusFrequency] = useState(null);

  const [matrixEqualizerData, setMatrixEqualizerData] = useState(null);

  const [satoriMatrix, setSatoriMatrix] = useState(null);

  const firstVisitTs = Number(localStorage.getItem('hearen_first_visit_ts') || 0);

  const daysSinceFirstVisit = firstVisitTs
    ? Math.floor((Date.now() - firstVisitTs) / (1000 * 60 * 60 * 24))
    : 0;
  useEffect(() => {
  const loadEqualizerCompletionState = async () => {
    if (!user?.id) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/equalizer/${user.id}`
      );

      if (!response.ok) {
        console.warn('[HEAREN MATRIX][TESTS COMPLETED] equalizer fetch failed', response.status);
        return;
      }

      const data = await response.json();

      const completed = Boolean(
        data?.has_audiometric_compensation ||
        data?.has_inverse_notch ||
        data?.audiometric_gains?.length > 0 ||
        data?.inverse_notch?.center_frequency
      );

      setTestsCompletedFromEqualizer(completed);

      console.log('[HEAREN MATRIX][TESTS COMPLETED]', {
        completed,
        hasAudiometric: data?.has_audiometric_compensation,
        hasNotch: data?.has_inverse_notch,
      });
    } catch (error) {
      console.warn('[HEAREN MATRIX][TESTS COMPLETED] error', {
        message: error?.message,
        name: error?.name,
        apiBaseUrl: API_BASE_URL,
      });
    }
  };

  loadEqualizerCompletionState();
  }, [user?.id]);

  useEffect(() => {
  const loadMatrixListeningStats = async () => {
    if (!user?.id) return;

    try {
  const response = await fetch(
    `${API_BASE_URL}/api/therapy-sessions/stats/${user.id}`
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const stats = await response.json();

  const totalSessions = Number(stats?.total_sessions || 0);
  const totalMinutes = Number(stats?.total_duration_minutes || 0);

  setMatrixListeningStats(prev => ({
    ...prev,
    sessionsCount: totalSessions,
    totalMinutes,
  }));

  console.log('[HEAREN MATRIX][LISTENING STATS]', {
    source: 'stats',
    sessionsCount: totalSessions,
    totalMinutes,
  });
    } catch (error) {
      console.warn('[HEAREN MATRIX][LISTENING STATS] error', {
        message: error?.message,
      });
    }
  };

  loadMatrixListeningStats();
}, [user?.id]);

useEffect(() => {
  const loadEqualizerData = async () => {
    try {
      if (!user?.id) return;

      const response = await fetch(
        `${API_BASE_URL}/api/equalizer/${user.id}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const equalizerData = await response.json();

      setMatrixEqualizerData(equalizerData);


    } catch (error) {
      console.warn('[SATORI][PROFILE][SOURCE_ERROR]', {
        message: error?.message,
      });
    }
  };

  loadEqualizerData();
}, [user?.id]);

useEffect(() => {
  const loadMatrixTinnitusStatus = async () => {
      if (!user?.id) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/therapy-sessions/user/${user.id}`
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const sessions = await response.json();

        if (!Array.isArray(sessions)) return;

        const totalSeconds = sessions.reduce(
          (sum, session) => sum + (session.duration || 0),
          0
        );

        const activeDays = new Set(
          sessions
            .map((session) => session.date)
            .filter(Boolean)
            .map((date) => new Date(date).toISOString().split('T')[0])
        ).size;

        setMatrixListeningStats({
          sessionsCount: sessions.length,
          totalMinutes: Math.round(totalSeconds / 60),
          activeDays,
        });

        console.log('[HEAREN MATRIX][LISTENING STATS]', {
          sessionsCount: sessions.length,
          totalMinutes: Math.round(totalSeconds / 60),
          activeDays,
        });
      } catch (error) {
        console.warn('[HEAREN MATRIX][LISTENING STATS] error', {
          message: error?.message,
        });
      }
    };

  }, [user?.id]);

useEffect(() => {
  console.log('[HEAREN MATRIX][TINNITUS STATUS][EFFECT START]', {
    userId: user?.id,
  });

  const loadMatrixTinnitusStatus = async () => {
    try {
      const token =
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('auth_token');

      console.log('[HEAREN MATRIX][TINNITUS STATUS][FETCH START]', {
        hasToken: Boolean(token),
      });

      const controller = new AbortController();

      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 8000);

      const response = await fetch(`${API_BASE_URL}/api/lab/programs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log('[HEAREN MATRIX][TINNITUS STATUS][FETCH RESPONSE]', {
        status: response.status,
      });

      if (response.status === 403) {
        console.warn('[HEAREN MATRIX][TINNITUS STATUS][FORBIDDEN]', {
          status: response.status,
          reason: 'Lab programs not available for this user or environment',
        });

  setMatrixTinnitusFrequency(null);
  return;
}

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const programs = await response.json();

      const tinnitusFrequency =
        programs?.notch?.f_t ||
        programs?.inverse_notch?.f_t ||
        null;

      setMatrixTinnitusFrequency(tinnitusFrequency);

      console.log('[HEAREN MATRIX][TINNITUS STATUS]', {
        tinnitusFrequency,
        tinnitusTestCompleted: Boolean(
          tinnitusFrequency && tinnitusFrequency > 0
        ),
      });
    } catch (error) {
      console.warn('[HEAREN MATRIX][TINNITUS STATUS][SKIP]', {
        message: error?.message,
        name: error?.name,
      });

      setMatrixTinnitusFrequency(null);
    }
  };

  loadMatrixTinnitusStatus();
}, [user?.id]);

const audioTestCompleted = Boolean(
  testsCompletedFromEqualizer ||
  adaptive?.debug?.hasCompletedPersonalization ||
  adaptive?.completed ||
  adaptive?._fullState?.completed ||
  user?.hearingTestCompleted ||
  user?.audioProfileCompleted ||
  user?.hasAudioProfile ||
  localStorage.getItem('hearen_audio_profile_completed') === 'true' ||
  localStorage.getItem('hearen_hearing_test_completed') === 'true'
);

const resolvedTinnitusFrequency =
  matrixTinnitusFrequency ||
  matrixEqualizerData?.tinnitus_frequency ||
  matrixEqualizerData?.inverse_notch?.center_frequency ||
  null;

const tinnitusTestCompleted = Boolean(
  (resolvedTinnitusFrequency && Number(resolvedTinnitusFrequency) > 0) ||
  matrixEqualizerData?.has_inverse_notch ||
  user?.tinnitusTestCompleted ||
  user?.hasTinnitusProfile ||
  localStorage.getItem('hearen_tinnitus_test_completed') === 'true'
);

useEffect(() => {
  try {
    const hearenProfile = buildHearenProfileConfig({
      user,
      adaptive,
      audioProfileCompleted: audioTestCompleted,
      tinnitus: {
        frequency: resolvedTinnitusFrequency || null,
        completed: tinnitusTestCompleted
      },
      listeningStats: {
        totalMinutes:
          matrixListeningStats?.totalMinutes ||
          user?.totalListeningMinutes ||
          user?.listeningStats?.totalMinutes ||
          0
      }
    });

    const satoriMatrix = buildSatoriMatrix(hearenProfile);

      setSatoriMatrix(satoriMatrix);

      console.log('[SATORI][MATRIX]', satoriMatrix);
      console.log('[SATORI][PROFILE]', hearenProfile);


    console.log('[SATORI][PROFILE]', hearenProfile);
  } catch (error) {
    console.warn('[SATORI][PROFILE][SKIP]', {
      message: String(error?.message || error),
      name: error?.name || null,
    });
  }
}, [
  user,
  adaptive,
  audioTestCompleted,
  tinnitusTestCompleted,
  resolvedTinnitusFrequency,
  matrixListeningStats?.totalMinutes
]);

const profileStatus =
  audioTestCompleted && tinnitusTestCompleted
    ? 'complete'
    : audioTestCompleted || tinnitusTestCompleted
      ? 'partial'
      : 'none';


const matrixSignals = {
    sessionsCount:
      matrixListeningStats.sessionsCount ||
      adaptive?.debug?.sessionCount ||
      user?.sessionsCount ||
      user?.sessionCount ||
      (firstVisitTs ? 1 : 0),

    totalMinutes:
      matrixListeningStats.totalMinutes ||
      adaptive?.debug?.totalMinutes ||
      user?.totalMinutes ||
      user?.totalListeningMinutes ||
      0,

    activeDays:
      matrixListeningStats.activeDays || daysSinceFirstVisit || 0,

    currentSessionMinutes: 0,

      audioTestCompleted,
      tinnitusTestCompleted,
      profileStatus,

      testsCompleted: profileStatus !== 'none',

      currentHour: new Date().getHours(),
      lowInteraction: false,
    };

    const matrixState = resolveHearenState(matrixSignals);

  console.log('[HEAREN MATRIX][HOME SIGNALS]', {
    matrixSignals,
    matrixState,
  });

  const [isReady, setIsReady] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const scrollRef = useRef(null);

  const testProfileEQOn = async () => {
  await NativeAudio.setMultiBandEQ({
    enabled: true,
    bands: [
  { frequency: 120, gain: 18.0, bandwidth: 1.0 },
  { frequency: 800, gain: -18.0, bandwidth: 1.0 },
  { frequency: 3000, gain: 18.0, bandwidth: 1.0 },
  { frequency: 8000, gain: 18.0, bandwidth: 1.0 }
]
  });
  console.log('[EQ PROFILE TEST] ON');
};

const testProfileEQOff = async () => {
  await NativeAudio.setMultiBandEQ({
    enabled: false,
    bands: []
  });
  console.log('[EQ PROFILE TEST] OFF');
};

  // Header mount animation — trigger only on first mount
  const [headerMounted, setHeaderMounted] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setForceRefresh(p => p + 1);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const setupCapacitorListener = async () => {
      try {
        const { App } = await import('@capacitor/app');
        const listener = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) setForceRefresh(p => p + 1);
        });
        return () => listener.remove();
      } catch (e) { /* web */ }
    };
    setupCapacitorListener();

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, [forceRefresh]);

  useEffect(() => {
    if (isReady && !headerMounted) {
      requestAnimationFrame(() => setHeaderMounted(true));
    }
  }, [isReady, headerMounted]);

  const handleThemeTap = async (theme) => {
    console.log('[HOME] Theme tap', {
      id: theme?.id,
      title: theme?.title || theme?.name,
      free: theme?.free,
      comingSoon: theme?.comingSoon,
      isPremium
    });

  if (theme.comingSoon) return;
  if (!theme.free && !isPremium) {
    navigate('/premium');
    return;
  }

  const stopHandler = getSessionStopHandler();

  console.log('[HOME THEME TAP]', {
    theme: theme.id,
    hasStopHandler: !!stopHandler,
  });

  if (stopHandler) {
    await stopHandler('theme-change');
  }

  trackSessionStart(theme.id);
  setForceRefresh(p => p + 1);
  navigate(`/therapy?theme=${theme.id}&autoplay=1`);
};

  const resolveLabel = (theme) => {
    const key = theme.id === 'pulse_flow' ? 'pulseFlow'
      : theme.id === 'chill_lofi' ? 'lofi'
      : theme.id === 'deep_calm' ? 'deepCalm'
      : theme.id;
    return t?.themes?.[key] || theme.fallbackLabel;
  };

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a1a' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white/10 border-t-white/40 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-white/40 text-sm">{t?.loading || 'Chargement...'}</p>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-screen relative" style={{ background: '#0a0a1a' }} data-testid="home-screen">

    <div className="relative z-10 pt-14 pb-36 px-5">

  {/* Header — progressive appearance */}
  <h1
    className="text-[1.65rem] font-semibold text-white tracking-[-0.01em] leading-[1.15]"
    style={{
      opacity: headerMounted ? 1 : 0,
      transform: headerMounted ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      letterSpacing: '-0.01em',
    }}
    data-testid="home-title"
  >
    {isFr ? 'Entendre autrement' : 'Hear differently'}
  </h1>

  <p
    className="text-[0.9rem] font-normal text-white/40 mt-2.5 mb-10"
    style={{
      opacity: headerMounted ? 1 : 0,
      transform: headerMounted ? 'translateY(0)' : 'translateY(8px)',
      transition: 'opacity 0.5s ease-out 0.15s, transform 0.5s ease-out 0.15s',
      lineHeight: '1.4',
    }}
    data-testid="home-subtitle"
  >
    {isFr ? 'Un son qui s’adapte à vous' : 'Sound that adapts to you'}
  </p>

        {/* Soundscapes */}
        <p className="text-white/30 text-[11px] font-medium uppercase tracking-wider mb-3" data-testid="soundscapes-label">
          {isFr ? 'Ambiances' : 'Soundscapes'}
        </p>
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-4 -mx-5 px-5 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
          data-testid="theme-scroll"
        >
          {V11_THEMES.map((theme) => {
            const locked = !theme.free && !isPremium;
            const soon = !!theme.comingSoon;
            const label = resolveLabel(theme);

            return (
              <button
                key={theme.id}
                onClick={() => handleThemeTap(theme)}
                className="relative flex-shrink-0 snap-start rounded-2xl overflow-hidden transition-transform active:scale-[0.97]"
                style={{ width: '140px', height: '190px' }}
                data-testid={`theme-card-${theme.id}`}
              >
                <div className="absolute inset-0">
                  <img
                    src={theme.bgImage}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.05) 100%)',
                  }} />
                </div>

                <div className="relative z-10 flex flex-col justify-end h-full p-3">
                  <p className="text-white font-semibold text-sm text-left leading-snug drop-shadow-lg">
                    {label}
                  </p>
                  {soon && (
                    <span className="text-white/45 text-[10px] text-left mt-0.5">
                      {t?.comingSoon || 'Bientot'}
                    </span>
                  )}
                </div>

                {locked && !soon && (
                  <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/10">
                    <Lock size={11} className="text-white/70" />
                  </div>
                )}

                {soon && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20">
                    <span className="text-white/70 text-[11px] font-medium px-3 py-1 rounded-full bg-white/8 backdrop-blur-sm border border-white/10">
                      {t?.comingSoon || 'Bientot'}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {/* Cocoon Card */}
        <div className="mb-8" data-testid="main-entries">
          <button
            onClick={() => {
              setIsOpeningCocoon(true);
              navigate('/night-mode');
            }}
            className="w-full text-left rounded-2xl overflow-hidden transition-transform active:scale-[0.97] relative"
            style={{ height: '76px' }}
            data-testid="entry-cocoon"
          >
            <div className="absolute inset-0 overflow-hidden rounded-2xl">
              <img
                src="/themes/cocoon.webp"
                alt=""
                className="w-full h-full object-cover"
                style={{
                  animation: 'cocoonBreathe 10s ease-in-out infinite',
                  willChange: 'transform, filter',
                }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(30,20,60,0.55) 0%, rgba(10,10,26,0.7) 100%)',
                  animation: 'cocoonOverlayPulse 10s ease-in-out infinite',
                }}
              />
            </div>
            <div className="relative z-10 flex flex-col justify-end h-full p-3">
              <div className="flex items-center gap-2">
                {isOpeningCocoon && (
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
                )}

                <p className="text-white font-semibold text-base drop-shadow-lg">
                  {isOpeningCocoon
                    ? (isFr ? 'Préparation du refuge...' : 'Opening Cocoon...')
                    : 'Cocoon'}
                </p>
              </div>
            </div>
          </button>
        </div>
        {/* Nudge */}
  <PersonalizationNudge
    nudge={{
      ...adaptive.nudge,

      title: isFr
        ? satoriMatrix?.home?.titleFr || t?.matrix?.[matrixState?.suggestionKey] || adaptive.nudge?.title
        : satoriMatrix?.home?.titleEn || t?.matrix?.[matrixState?.suggestionKey] || adaptive.nudge?.title,

      subtitle: isFr
        ? `✨ Hera · ${satoriMatrix?.home?.subtitleFr || t?.matrix?.[matrixState?.heraAdviceKey] || ''}`
        : `✨ Hera · ${satoriMatrix?.home?.subtitleEn || t?.matrix?.[matrixState?.heraAdviceKey] || ''}`,

      cta: t?.matrix?.adjust_listening || 'Ajuster mon écoute →',
    }}
    glow={adaptive.glow}
    animation={adaptive.animation}
    fullState={adaptive._fullState}
    isSimulation={adaptive.isSimulation}
  />
      </div>

      <div className="fixed bottom-0 left-0 right-0 h-24 pointer-events-none" style={{
        background: 'linear-gradient(0deg, rgba(10,10,26,0.95) 0%, transparent 100%)',
        zIndex: 5,
      }} />
    </div>
  );
};

export default HomeNew;
